import { Node } from "reactflow"
import { GraphNodeChange } from "./changes/graphApplyChanges"
import { GraphNodeEdgeData } from "@store/services/converter/types"
import { GraphNodeType } from "@custom-types/graphs"

export enum GraphEditMode {
  OFF = 1,
  SCENARIO,
  DATA, // rename to something more fiting once it'll be implemented
  BUILD,
}

interface ClearChanges {
  nodeChanges: { [key: string]: GraphNodeChange }
}

export interface GraphNodeSelection {
  nodes: Node[]
  isExpandingIconClicked: boolean
}

export interface GroupNodeLabel {
  key: string
  label: string
  type: GraphNodeType
  group: string
  sort: string
  format?: string
}

/**
 * @deprecated GraphDataViewModel is a static singleton class used as global mutable state.
 * Properties mutated here are NOT tracked by React, so components reading them won't re-render
 * reliably when values change — they only pick up new values on the next render triggered by
 * something else (e.g. a parent setState call).
 *
 * TODO: migrate all fields to proper reactive state (Jotai atoms or React context) and
 *       remove this class entirely.
 */
export class GraphDataViewModel {
  private static graphData?: GraphNodeEdgeData
  private static groupNodeLabels: GroupNodeLabel[] = []
  private static onLoadCallbacks: (() => void)[] = []
  public static isExpandingIconClicked: boolean = false

  static setGraphData(graphData: GraphNodeEdgeData): void {
    this.graphData = graphData
    this.groupNodeLabels = convertGraphDataToGroupLabels(graphData)
    this.onLoadCallbacks.forEach((callback) => callback())
    this.onLoadCallbacks = []
  }

  static get data(): GraphNodeEdgeData | undefined {
    return this.graphData
  }

  static get groupLabels(): GroupNodeLabel[] {
    return this.groupNodeLabels
  }

  static clear(): void {
    this.graphData = undefined
    this.groupNodeLabels = []
    this.onLoadCallbacks = []
    this.isExpandingIconClicked = false
  }

  static onLoad(callback: () => void): void {
    this.onLoadCallbacks.push(callback)
  }
}

export class GraphSelectionModel {
  static selected?: string = undefined
  static child: Set<string> = new Set()
  static childEdges: Set<string> = new Set()
  static parent: Set<string> = new Set()
  static parentEdges: Set<string> = new Set()

  static clear(): void {
    this.selected = undefined
    this.child.clear()
    this.parent.clear()
    this.childEdges.clear()
    this.parentEdges.clear()
  }

  static clearSelectionChanges(): ClearChanges {
    const nodeChanges: { [key: string]: GraphNodeChange } = {}
    if (GraphSelectionModel.selected) {
      nodeChanges[GraphSelectionModel.selected] = {
        id: GraphSelectionModel.selected,
        selected: false,
      }
    }

    GraphSelectionModel.parent.forEach((id) => {
      nodeChanges[id] = { id, selected: false }
    })
    GraphSelectionModel.child.forEach((id) => {
      nodeChanges[id] = { id, selected: false }
    })
    return { nodeChanges }
  }
}

const convertGraphDataToGroupLabels = (data: GraphNodeEdgeData): GroupNodeLabel[] => {
  const labels: GroupNodeLabel[] = []

  Object.values(data.groupMap).forEach((g) => {
    g.group.nodes.forEach((nodeId) => {
      const label = data.labelMap[nodeId]
      const node = data.nodeMap[nodeId]

      if (!label || !node) return

      labels.push({
        key: nodeId,
        group: g.group.label,
        label,
        sort: g.group.label,
        type: node.type,
        format: node.display["Unit Format"],
      })
    })
  })
  const notGroupedNodes = Object.values(data.nodeMap).filter(
    (n) => !n.display.groups || n.display.groups.length === 0,
  )
  notGroupedNodes.forEach((node) => {
    labels.push({
      key: node.node?.name || "",
      group: "---",
      label: node.display.label,
      sort: "zzzzzzzzz", // sorting hack to force ungrouped elements to be last on list
      type: node.node!.type,
      format: node.display["Unit Format"],
    })
  })

  return labels.sort((a, b) => -b.sort.localeCompare(a.sort))
}
